import { useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { colors } from 'constants/theme'
import { dateTimeToView } from 'utils/date.utils'
import { getPdfMake } from 'component/Pdf/pdfMakeThai'

const DropdownDocument = styled(Dropdown)(() => ({
    '.dropdown-toggle': {
        backgroundColor: `${colors.white} !important`,
        color: `${colors.themeSecondColor} !important`,
        border: `1px solid ${colors.themeSecondColor} !important`,
        display: 'flex',
        alignItems: 'center',
        boxShadow: 'none !important',
        height: 40,
        '&:after': { marginLeft: '0.5rem' },
        '&[aria-expanded="true"]:after': { transform: 'rotate(180deg)' },
    },
    '.dropdown-menu': {
        marginBottom: 4,
        borderRadius: 4,
        background: colors.white,
        border: `1px solid ${colors.lightGray}`,
        minWidth: '130px',
        '.dropdown-item': {
            '&:hover': {
                backgroundColor: colors.themeSecondColor06,
                color: colors.themeSecondColor,
            },
        },
    },
}))

type Props = {
    fetchRows: () => Promise<any[]>
    search: string
    statusSelect: string
    getConditionSummary: (row: any) => string
    fileName?: string
    title?: string
}

type RenderContext = Omit<Props, 'fetchRows'> & { rows: any[] }

const buildColumns = (t: (k: string, opts?: any) => string) => [
    { key: 'no', header: '#', width: 6, pdfWidth: 18 },
    { key: 'promotionTypeName', header: t('PROMOTION.TABLE.PROMOTION_TYPENAME'), width: 22, pdfWidth: 55 },
    { key: 'promotionCode', header: t('PROMOTION.TABLE.PROMOTION_CODE'), width: 16, pdfWidth: 50 },
    { key: 'promotionName', header: t('PROMOTION.TABLE.PROMOTION_NAME_TH'), width: 28, pdfWidth: 70 },
    { key: 'promotionNameEn', header: t('PROMOTION.TABLE.PROMOTION_NAMEEN'), width: 24, pdfWidth: 60 },
    { key: 'condition', header: t('VOUCHER.TABLE.CONDITION') || 'เงื่อนไข', width: 40, pdfWidth: '*' as any },
    { key: 'promotionFormat', header: t('PROMOTION.TABLE.TYPE'), width: 16, pdfWidth: 45 },
    { key: 'branch', header: t('PROMOTION.TABLE.USAGE_BRANCH'), width: 24, pdfWidth: 60 },
    { key: 'count', header: t('PROMOTION.TABLE.COUNT'), width: 10, pdfWidth: 30 },
    { key: 'dateStart', header: t('PROMOTION.TABLE.USAGE_DATE'), width: 20, pdfWidth: 55 },
    { key: 'status', header: t('APPLICATION.TABLE.CELL.STATUS'), width: 14, pdfWidth: 40 },
    { key: 'updatedBy', header: t('APPLICATION.TABLE.CELL.UPDATED_BY'), width: 24, pdfWidth: 60 },
]

const branchLabel = (row: any) => {
    const bs = row?.promotionBranchs
    if (!Array.isArray(bs) || bs.length === 0) return '-'
    return bs.map((b: any) => b?.branchName).filter(Boolean).join(', ') || '-'
}

const mapRow = (
    row: any,
    index: number,
    t: (k: string, opts?: any) => string,
    getConditionSummary: (row: any) => string,
) => {
    const statusLabel = row?.status ? t(`PROMOTION.STATUS.${row.status}`) : '-'
    const typeLabel = row?.type ? t(`PROMOTION.TYPE.${row.type}`) : '-'
    const updatedAt = row?.updatedAt ? dateTimeToView(row.updatedAt, false) : ''
    const updatedBy = [row?.updatedBy || '-', updatedAt].filter(Boolean).join('\n')
    return {
        no: index + 1,
        promotionTypeName: row?.promotionType?.promotionTypeName || '-',
        promotionCode: row?.promotionCode || '-',
        promotionName: row?.promotionName || '-',
        promotionNameEn: row?.promotionNameEn || '-',
        condition: getConditionSummary(row) || '-',
        promotionFormat: typeLabel,
        branch: branchLabel(row),
        count: row?.amountPromotion ?? '-',
        dateStart: row?.promotionDate || '-',
        status: statusLabel,
        updatedBy,
    }
}

const statusFilterLabel = (statusSelect: string, t: (k: string) => string) => {
    if (!statusSelect || statusSelect === 'ALL') return t('ALL')
    return t(`PROMOTION.STATUS.${statusSelect}`)
}

async function exportExcel(props: RenderContext, t: (k: string, opts?: any) => string) {
    const cols = buildColumns(t)
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(props.title || t('PROMOTION.TITLE'), {
        pageSetup: { orientation: 'landscape', paperSize: 9 },
    })
    ws.properties.defaultRowHeight = 20
    ws.columns = cols.map((c) => ({ header: c.header, key: c.key, width: c.width })) as any

    let rowIdx = 1

    ws.mergeCells(rowIdx, 1, rowIdx, cols.length)
    ws.getCell(rowIdx, 1).value = props.title || t('PROMOTION.TITLE')
    ws.getCell(rowIdx, 1).font = { size: 16, bold: true }
    ws.getCell(rowIdx, 1).alignment = { horizontal: 'center' }
    rowIdx++

    ws.mergeCells(rowIdx, 1, rowIdx, cols.length)
    ws.getCell(rowIdx, 1).value = [
        `${t('INPUT.SEARCH')}: ${props.search || '-'}`,
        `${t('APPLICATION.TABLE.CELL.STATUS')}: ${statusFilterLabel(props.statusSelect, t)}`,
        `${t('PROMOTION.TABLE.COUNT')}: ${props.rows.length}`,
    ].join('    ')
    ws.getCell(rowIdx, 1).font = { size: 11 }
    ws.getCell(rowIdx, 1).alignment = { horizontal: 'left' }
    rowIdx += 2

    const headerRow = ws.addRow(cols.map((c) => c.header))
    headerRow.height = 24
    headerRow.font = { bold: true }
    headerRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    headerRow.eachCell((cell) => {
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        }
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF1F3F5' },
        }
    })

    if (!props.rows || props.rows.length === 0) {
        const empty = ws.addRow([t('NOT_FOUND')])
        ws.mergeCells(empty.number, 1, empty.number, cols.length)
        empty.alignment = { horizontal: 'center' }
        empty.font = { italic: true }
    } else {
        const centerKeys = new Set(['no', 'promotionFormat', 'count', 'dateStart', 'status', 'updatedBy'])
        props.rows.forEach((row, i) => {
            const values = mapRow(row, i, t, props.getConditionSummary)
            const dataRow = ws.addRow(cols.map((c) => (values as any)[c.key]))
            dataRow.alignment = { vertical: 'middle', wrapText: true }
            dataRow.eachCell((cell, colNumber) => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                }
                const colKey = cols[colNumber - 1].key
                if (centerKeys.has(colKey)) {
                    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
                }
            })
        })
    }

    const buf = await wb.xlsx.writeBuffer()
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    saveAs(blob, `${props.fileName || t('PROMOTION.TITLE')}_${moment().format('YYYY-MM-DD')}.xlsx`)
}

async function exportPdf(props: RenderContext, t: (k: string, opts?: any) => string) {
    const pdfMake = await getPdfMake()
    const cols = buildColumns(t)

    const header = cols.map((c) => ({
        text: c.header,
        style: 'tableHeader',
    }))

    const body: any[] = [header]
    if (!props.rows.length) {
        body.push([{ text: t('NOT_FOUND'), colSpan: cols.length, alignment: 'center', italics: true }])
    } else {
        const centerKeys = new Set(['no', 'promotionFormat', 'count', 'dateStart', 'status', 'updatedBy'])
        props.rows.forEach((row, i) => {
            const v = mapRow(row, i, t, props.getConditionSummary)
            body.push(
                cols.map((c) => {
                    const val = (v as any)[c.key]
                    return {
                        text: val == null || val === '' ? '-' : String(val),
                        alignment: centerKeys.has(c.key) ? 'center' : 'left',
                    }
                }),
            )
        })
    }

    const filterLine = [
        `${t('INPUT.SEARCH')}: ${props.search || '-'}`,
        `${t('APPLICATION.TABLE.CELL.STATUS')}: ${statusFilterLabel(props.statusSelect, t)}`,
        `${t('PROMOTION.TABLE.COUNT')}: ${props.rows.length}`,
    ].join('    ')

    const docDefinition: any = {
        pageSize: 'A4',
        pageOrientation: 'landscape',
        pageMargins: [20, 40, 20, 40],
        defaultStyle: { font: 'Sarabun', fontSize: 8 },
        content: [
            { text: props.title || t('PROMOTION.TITLE'), style: 'title' },
            { text: filterLine, style: 'filter' },
            {
                table: {
                    headerRows: 1,
                    widths: cols.map((c) => c.pdfWidth as any),
                    body,
                },
                layout: {
                    fillColor: (rowIndex: number) => (rowIndex === 0 ? '#F1F3F5' : null),
                    hLineWidth: () => 0.5,
                    vLineWidth: () => 0.5,
                    hLineColor: () => '#B0BEC5',
                    vLineColor: () => '#B0BEC5',
                },
            },
        ],
        styles: {
            title: { fontSize: 16, bold: true, alignment: 'center', margin: [0, 0, 0, 6] },
            filter: { fontSize: 10, margin: [0, 0, 0, 8] },
            tableHeader: { bold: true, fontSize: 8, alignment: 'center' },
        },
        footer: (currentPage: number, pageCount: number) => ({
            text: `${currentPage} / ${pageCount}`,
            alignment: 'center',
            fontSize: 8,
            margin: [0, 12, 0, 0],
        }),
    }

    const fileName = `${props.fileName || t('PROMOTION.TITLE')}_${moment().format('YYYY-MM-DD')}.pdf`
    pdfMake.createPdf(docDefinition).download(fileName)
}

export default function PromotionExportButton(props: Props) {
    const { t } = useTranslation()
    const [busy, setBusy] = useState(false)

    const withRows = async (): Promise<RenderContext> => {
        const rows = (await props.fetchRows()) || []
        const { fetchRows: _f, ...rest } = props
        return { ...rest, rows }
    }

    const handleExcel = async () => {
        if (busy) return
        setBusy(true)
        try {
            await exportExcel(await withRows(), t)
        } finally {
            setBusy(false)
        }
    }

    const handlePdf = async () => {
        if (busy) return
        setBusy(true)
        try {
            await exportPdf(await withRows(), t)
        } finally {
            setBusy(false)
        }
    }

    return (
        <DropdownDocument>
            <Dropdown.Toggle id="dropdown-promotion-export" className="ml-xl-auto" disabled={busy}>
                {t('PERSONNEL.BUTTON.PRINT_DOCUMENT')}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item onClick={handleExcel} disabled={busy}>
                    {t('Excel')}
                </Dropdown.Item>
                <Dropdown.Item onClick={handlePdf} disabled={busy}>
                    {t('PDF')}
                </Dropdown.Item>
            </Dropdown.Menu>
        </DropdownDocument>
    )
}
